'use client';

import Link from "next/link";
import { useState, useEffect } from 'react';
import Hero from "@/components/Hero";
import Section from "@/components/Section";
import OptimizedImage from "@/components/OptimizedImage";
import Head from "next/head";

export default function SupportTraining() {
  const [isLoaded, setIsLoaded] = useState(false);
  // State to track if component is mounted (for animations)
  const [isMounted, setIsMounted] = useState(false);

  // Load optimization for the hero image
  useEffect(() => {
    // Mark the page as loaded after initial render
    setIsLoaded(true);
    // Mark component as mounted for animations
    setIsMounted(true);
    
    // Preload the support image for faster rendering
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'image';
    preloadLink.href = '/images/ems-suit-support.jpg';
    preloadLink.fetchPriority = 'high';
    document.head.appendChild(preloadLink);
    
    // Add meta title and description tags dynamically
    document.title = "Comprehensive EMS Support & Training Program | EMS Pulse UK";
    
    // Find existing description meta tag or create a new one
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', "Our dedicated UK team ensures your success with expert EMS training and ongoing support. From setup to operations, we provide complete guidance for your I-motion EMS business.");
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section using the systems page Tower reference styling */}
      <section className="relative py-20 sm:py-24 md:py-28 px-4 bg-gradient-to-br from-[#1a2942] via-[#234563] to-[#1e5e52] text-white overflow-hidden min-h-[460px] !min-h-[460px] sm:min-h-0"
        style={{ minHeight: '460px' }}
      >
        {/* Abstract shapes */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-1/4 w-60 sm:w-80 md:w-96 h-60 sm:h-80 md:h-96 rounded-full bg-[#38b5a0] blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-1/5 w-60 sm:w-70 md:w-80 h-60 sm:h-70 md:h-80 rounded-full bg-[#f8c156] blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-40 left-1/3 w-40 sm:w-50 md:w-60 h-40 sm:h-50 md:h-60 rounded-full bg-[#38b5a0]/40 blur-3xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <span 
            className="inline-block bg-[#38b5a0]/20 text-[#f8fffc] text-lg mb-4 py-1 px-4 rounded-full border border-[#38b5a0]/30 animate-[fadeSlideUp_0.7s_0.2s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            UK-Based Support
          </span>
          <h1 
            className="text-3xl md:text-5xl font-bold mb-6 leading-tight animate-[fadeSlideUp_0.7s_0.4s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Comprehensive Support & Training Program
          </h1>
          <p 
            className="text-xl opacity-90 max-w-3xl mx-auto animate-[fadeSlideUp_0.7s_0.6s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Our dedicated UK team ensures you get the most from your I-motion EMS system with expert training and ongoing support.
          </p>
        </div>
        
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden">
          <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-[60px]">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-white"></path>
          </svg>
        </div>
      </section>

      {/* Define the animations so they're available in this component */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeSlideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Our Approach to Support Section */}
      <Section 
        background="light"
        mobilePadding="default"
      >
        <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8 md:gap-10">
          <div className="md:w-1/2">
            <h2 className="text-xl sm:text-2xl md:text-4xl font-bold mb-4 sm:mb-6 text-[#1a2942] relative">
              <span className="inline-block bg-[#38b5a0] h-1 w-16 absolute -top-3 sm:-top-4"></span>
              Partnership-Focused Support
            </h2>
            <p className="text-base sm:text-lg text-[#455a73] mb-3 sm:mb-4">
              At EMS Pulse, we view our relationship with your business as a
              long-term partnership. As the exclusive UK distributor for
              I-motion systems, we provide not just superior technology, but
              comprehensive, ongoing support to ensure your success.
            </p>
            <p className="text-base sm:text-lg text-[#455a73]">
              Our UK-based team of EMS specialists is available to assist with
              everything from initial setup to business development
              strategies, ensuring you get the maximum return on your
              investment.
            </p>
          </div>

          {/* Support Team Image with optimized component */}
          <div className="md:w-1/2 w-full rounded-xl shadow-lg overflow-hidden mt-4 sm:mt-6 md:mt-0">
            <div className="relative" style={{ aspectRatio: '800/600' }}>
              <OptimizedImage 
                src="/images/ems-suit-support.jpg"
                alt="EMS Pulse support team providing training and assistance"
                width={800}
                height={600}
                priority
                sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, 50vw"
                fetchPriority="high"
                performanceMarkName="first-contentful-image-loaded"
                className="w-full h-full object-cover"
                mobileBehavior="adjust"
                mobileAspectRatio="4/3"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#38b5a0]/20 to-transparent pointer-events-none"></div>
              <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4">
                <div className="bg-[#38b5a0]/20 backdrop-blur-sm text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-full border border-[#38b5a0]/30 shadow-sm">
                  <span className="font-medium text-xs sm:text-sm">UK-Based Management</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Support Package Features */}
      <Section 
        background="gradient"
        title="Your Complete Support Solution"
        description="Everything you need to successfully implement and grow your EMS offering"
        tagText="COMPREHENSIVE PACKAGE"
        centerText={true}
        mobilePadding="compact"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-4 sm:p-6 md:p-8 transition-all duration-300 hover:-translate-y-1">
            <div className="bg-[#38b5a0] p-2 sm:p-3 rounded-xl text-white inline-block mb-3 sm:mb-4 shadow-md shadow-[#38b5a0]/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z"
                />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">
              Initial Setup & Installation
            </h3>
            <p className="text-sm sm:text-base text-[#455a73]">
              Professional installation of your I-motion system by our
              certified technicians, ensuring optimal performance from day
              one.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-4 sm:p-6 md:p-8 transition-all duration-300 hover:-translate-y-1">
            <div className="bg-[#2389db] p-2 sm:p-3 rounded-xl text-white inline-block mb-3 sm:mb-4 shadow-md shadow-[#2389db]/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
                />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">
              Comprehensive Training
            </h3>
            <p className="text-sm sm:text-base text-[#455a73]">
              In-depth certification program for your team, covering system
              operation, client management, session design, and safety
              protocols.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-4 sm:p-6 md:p-8 transition-all duration-300 hover:-translate-y-1">
            <div className="bg-[#f8c156] p-2 sm:p-3 rounded-xl text-white inline-block mb-3 sm:mb-4 shadow-md shadow-[#f8c156]/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">
              Ongoing Technical Support
            </h3>
            <p className="text-sm sm:text-base text-[#455a73]">
              Direct access to our UK technical team for rapid resolution of
              any questions or issues that may arise with your system.
            </p>
          </div>
        </div>
      </Section>

      {/* Training Program Details */}
      <Section
        title="EMS Pulse Training Certification Program"
        className="relative overflow-hidden"
        mobilePadding="default"
      >
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-full h-full bg-[url('/pattern.svg')] bg-no-repeat bg-cover"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-start relative z-10">
          <div className="space-y-4 sm:space-y-6 md:space-y-8">
            <div className="bg-slate-50 p-4 sm:p-5 md:p-6 rounded-xl border border-slate-200">
              <div className="flex gap-3 sm:gap-4 items-start">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#38b5a0]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#38b5a0] font-bold text-sm sm:text-base">01</span>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-[#1a2942]">
                    System Fundamentals
                  </h3>
                  <p className="text-sm sm:text-base text-[#455a73]">
                    Learn the core principles of I-motion EMS technology,
                    system components, setup procedures, and basic
                    troubleshooting.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-4 sm:p-5 md:p-6 rounded-xl border border-slate-200">
              <div className="flex gap-3 sm:gap-4 items-start">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#f8c156]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#f8c156] font-bold text-sm sm:text-base">02</span>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-[#1a2942]">
                    Client Assessment & Safety
                  </h3>
                  <p className="text-sm sm:text-base text-[#455a73]">
                    Develop skills in client screening, contraindication
                    identification, proper electrode placement, and emergency
                    protocols.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-4 sm:p-5 md:p-6 rounded-xl border border-slate-200">
              <div className="flex gap-3 sm:gap-4 items-start">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#2389db]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#2389db] font-bold text-sm sm:text-base">03</span>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-[#1a2942]">
                    Session Programming
                  </h3>
                  <p className="text-sm sm:text-base text-[#455a73]">
                    Master the software interface to create customized
                    training programs for different client goals and fitness
                    levels.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 sm:space-y-6 md:space-y-8">
            <div className="bg-slate-50 p-4 sm:p-5 md:p-6 rounded-xl border border-slate-200">
              <div className="flex gap-3 sm:gap-4 items-start">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#e05c88]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#e05c88] font-bold text-sm sm:text-base">04</span>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-[#1a2942]">
                    Practical Coaching Skills
                  </h3>
                  <p className="text-sm sm:text-base text-[#455a73]">
                    Develop effective coaching techniques, proper exercise
                    selection, and real-time intensity management during EMS
                    sessions.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-4 sm:p-5 md:p-6 rounded-xl border border-slate-200">
              <div className="flex gap-3 sm:gap-4 items-start">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#38b5a0]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#38b5a0] font-bold text-sm sm:text-base">05</span>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-[#1a2942]">
                    Business Integration
                  </h3>
                  <p className="text-sm sm:text-base text-[#455a73]">
                    Learn strategies for effective EMS service pricing,
                    marketing, client acquisition, and integration with
                    existing services.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-4 sm:p-5 md:p-6 rounded-xl border border-slate-200">
              <div className="flex gap-3 sm:gap-4 items-start">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#f8c156]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#f8c156] font-bold text-sm sm:text-base">06</span>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-[#1a2942]">
                    Certification Assessment
                  </h3>
                  <p className="text-sm sm:text-base text-[#455a73]">
                    Demonstrate your knowledge and skills through practical
                    and theoretical assessments to receive official
                    certification.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Business Development Support */}
      <Section 
        background="gradient"
        title="Business Development Support"
        description="We're invested in your long-term success with I-motion technology"
        tagText="GROWTH PARTNERSHIP"
        centerText={true}
        mobilePadding="compact"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
          {/* Cards with mobile optimization */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-4 sm:p-6 md:p-8 transition-all duration-300 hover:-translate-y-1">
            <div className="bg-[#f8c156] p-2 sm:p-3 rounded-xl text-white inline-block mb-3 sm:mb-4 shadow-md shadow-[#f8c156]/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
                />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">
              Marketing Resources
            </h3>
            <p className="text-sm sm:text-base text-[#455a73]">
              Gain access to professionally designed marketing materials,
              client education resources, and promotional strategies to
              effectively market your I-motion EMS services.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-4 sm:p-6 md:p-8 transition-all duration-300 hover:-translate-y-1">
            <div className="bg-[#38b5a0] p-2 sm:p-3 rounded-xl text-white inline-block mb-3 sm:mb-4 shadow-md shadow-[#38b5a0]/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V13.5zm0 2.25h.008v.008H8.25v-.008zm0 2.25h.008v.008H8.25V18zm2.498-6.75h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V13.5zm0 2.25h.007v.008h-.007v-.008zm0 2.25h.007v.008h-.007V18zm2.504-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zm0 2.25h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V18zm2.498-6.75h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V13.5zM8.25 6h7.5v2.25h-7.5V6zM12 2.25c-1.892 0-3.758.11-5.593.322C5.307 2.7 4.5 3.65 4.5 4.757V19.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V4.757c0-1.108-.806-2.057-1.907-2.185A48.507 48.507 0 0012 2.25z"
                />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">
              Programming Templates
            </h3>
            <p className="text-sm sm:text-base text-[#455a73]">
              Jumpstart your service offerings with our library of proven
              session templates for various client goals, from athletic
              performance to body toning.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-4 sm:p-6 md:p-8 transition-all duration-300 hover:-translate-y-1">
            <div className="bg-[#2389db] p-2 sm:p-3 rounded-xl text-white inline-block mb-3 sm:mb-4 shadow-md shadow-[#2389db]/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">
              Business Consultations
            </h3>
            <p className="text-sm sm:text-base text-[#455a73]">
              Scheduled strategy sessions with our business development team
              to optimize your EMS services, pricing structure, and client
              retention strategies.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-4 sm:p-6 md:p-8 transition-all duration-300 hover:-translate-y-1">
            <div className="bg-[#e05c88] p-2 sm:p-3 rounded-xl text-white inline-block mb-3 sm:mb-4 shadow-md shadow-[#e05c88]/10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3"
                />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">
              Software Updates & Upgrades
            </h3>
            <p className="text-sm sm:text-base text-[#455a73]">
              Regular updates to your I-motion software, ensuring you always
              have access to the latest features, program options, and system
              improvements.
            </p>
          </div>
        </div>
      </Section>

      {/* Call to Action */}
      <Section 
        background="dark" 
        className="relative overflow-hidden"
        mobilePadding="default"
      >
        {/* Abstract shapes */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 sm:top-20 right-1/4 w-40 sm:w-60 md:w-96 h-40 sm:h-60 md:h-96 rounded-full bg-[#32a895] blur-3xl"></div>
          <div className="absolute bottom-5 sm:bottom-10 left-1/5 w-40 sm:w-60 md:w-80 h-40 sm:h-60 md:h-80 rounded-full bg-[#32a895] blur-3xl"></div>
        </div>
        
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-[#38b5a0] rounded-full p-2 sm:p-3 text-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8"
            >
              <path
                fillRule="evenodd"
                d="M12.516 2.17a.75.75 0 0 0-1.032 0 11.209 11.209 0 0 1-7.877 3.08.75.75 0 0 0-.722.515A12.74 12.74 0 0 0 2.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 0 0 .374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 0 0-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08Zm3.094 8.016a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-lg sm:text-xl md:text-3xl font-bold text-white">
            Begin Your I-motion Partnership
          </h2>
        </div>

        <p className="text-sm sm:text-base md:text-lg mb-6 sm:mb-8 text-white opacity-90">
          Ready to transform your business with I-motion EMS technology? Our
          dedicated UK team is ready to discuss your specific needs, answer
          any questions about our support and training programs, and help
          you begin your journey toward offering cutting-edge EMS services
          to your clients.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <Link
            href="/contact"
            className="inline-block bg-[#38b5a0] text-white font-medium px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 md:py-4 rounded-lg hover:bg-[#2a8f7e] transition-all shadow-lg shadow-[#38b5a0]/20 text-center"
          >
            Request a Consultation
          </Link>
          <Link
            href="/roi"
            className="inline-block bg-white/10 text-white border border-white/20 font-medium px-4 sm:px-5 md:px-6 py-2.5 sm:py-3 md:py-4 rounded-lg hover:bg-white/20 transition-all shadow-md shadow-black/5 backdrop-blur-sm text-center"
          >
            Explore ROI & Investment
          </Link>
        </div>
      </Section>
    </div>
  );
}
